/**
 * Who a client may authorise their case to be discussed with.
 *
 * Mirrors `backend/src/config/consentDocuments.ts`. The frontend cannot import from the backend, so
 * this is the same arrangement `referralSources.ts` uses: one file on each side, and a test that
 * fails when they drift (`npm run test-consent-documents`).
 *
 * It lives here rather than inside `ConsentGate.tsx` because a list declared inside the one component
 * that renders it is how the referral-source dropdown ended up three copies deep, with two of them
 * missing entries that had been in the canonical list for months.
 *
 * ## Lloyd's wording, 16 Sep 2026
 *
 * These five and their labels are his, from "Section 2: Consent to Share Case Information" on the
 * acceptance screen he drafted. They replaced a four-item list, and the first one is a NEW permission
 * rather than a rename: reporting a matter to DataQs or the FMCSA system is not the same act as
 * telling the company that referred the client how their case is going.
 */

export type ConsentRecipient =
  | 'cdl-csa-reporting'
  | 'employer'
  | 'carrier'
  | 'payor'
  | 'other';

/**
 * What a stored answer may hold, which is wider than what a client can now choose.
 *
 * The server keeps two retired values in its union so rows written before 16 Sep still read, and
 * anything typing an API response has to allow for them or it is claiming the server cannot return
 * what it demonstrably can. They are deliberately absent from `RECIPIENT_ORDER`, so no screen can
 * offer one.
 */
export type StoredConsentRecipient =
  | ConsentRecipient
  | 'referring-organisation'
  | 'carrier-employer';

/** His labels, verbatim. These are what the client reads. */
export const RECIPIENT_LABELS: Record<ConsentRecipient, string> = {
  'cdl-csa-reporting': 'CDL / CSA reporting (e.g., DataQs, FMCSA-related reporting)',
  employer: 'My employer',
  carrier: 'My carrier',
  payor: 'An insurance payor',
  other: 'Other',
};

/** His order on the page. Not alphabetical, and not ours to reorder. */
export const RECIPIENT_ORDER: ConsentRecipient[] = [
  'cdl-csa-reporting',
  'employer',
  'carrier',
  'payor',
  'other',
];

/** The standing note above the boxes, in his words. */
export const SHARING_INTRO =
  'Certain matters, particularly CDL (commercial driver’s license) matters, may involve other ' +
  'parties who have a legitimate interest in your case, such as your employer, your carrier, an ' +
  'insurance payor, or the CDL/CSA reporting system itself. The Firm will only share your case ' +
  'information with the parties you approve below, and only for the matter this consent applies to.';

/** His sentence about changing an answer, which the decline path also has to honour. */
export const SHARING_CHANGE_NOTE =
  'You may change your answer at any time. Changes take effect immediately and apply going forward; ' +
  'they do not affect updates already sent under a prior consent.';

/** His heading for the block. */
export const SHARING_PROMPT =
  'I authorize the Firm to share case status updates for this matter (check all that apply):';
